import { ClerkProvider, useAuth as useClerkAuth } from '@clerk/clerk-react';
import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { StrictMode, useEffect, useMemo, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { ErrorBoundary } from '~/components/common/ErrorBoundary';
import { FullPageSpinner } from '~/components/common/FullPageSpinner';
import { Toaster } from '~/components/ui/sonner';
import { resolveConfig, setConfig } from '~/config';
import { useAuth } from '~/hooks/useAuth';
import { setAuthTokenGetter } from './lib/api-mutator';
import { handleQueryError } from './lib/authErrorGate';
import { createClerkTokenGetter } from './lib/authToken';
import { initSentry } from './lib/sentry';
import { createAppRouter } from './router';
import './styles/globals.css';
import './styles/brand-animations.css';

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: handleQueryError,
  }),
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 15, // 15 minutes
      refetchOnWindowFocus: false,
    },
  },
});

const router = createAppRouter();

interface RoutedAppProps {
  darkMode: boolean;
  toggleDarkMode: () => void;
}

// Builds the router context from live auth state. Must render inside the Clerk
// provider so `useAuth` resolves, and inside the error backstop so it wraps the
// entire routed tree. The WebSocket provider now lives in the root route (inside
// RouterProvider) because `useWebSocket` reads the router location.
//
// Holds the routed tree until Clerk finishes loading. The route guards
// deliberately defer (resolve without redirect) while `isLoaded` is false, so
// rendering routes during that window mounts authenticated pages whose queries
// fire before a token exists, producing spurious 401s. Wait for `isLoaded` so
// the first authenticated request already carries the token.
//
// Registers the API token getter here, during render, before handing off to
// RouterProvider. Route loaders (e.g. `onboardingBeforeLoad`) run in the commit
// layout phase, before any component mounts, so `AuthTokenBridge`'s effect-time
// registration lands too late for the initial load. Registering above the
// router guarantees the getter is in place before the first loader fires.
// oxlint-disable-next-line react/only-export-components
function RoutedApp({ darkMode, toggleDarkMode }: RoutedAppProps) {
  const auth = useAuth();
  const clerkAuth = useClerkAuth();

  const context = useMemo(
    () => ({ auth, queryClient, darkMode, toggleDarkMode }),
    [auth, darkMode, toggleDarkMode]
  );

  if (!auth.isLoaded) {
    return <FullPageSpinner />;
  }

  setAuthTokenGetter(createClerkTokenGetter(clerkAuth));

  return <RouterProvider router={router} context={context} />;
}

interface RootProps {
  publishableKey: string;
}

// Root component is not exported as it's only used for initial render
// oxlint-disable-next-line react/only-export-components
function Root({ publishableKey }: RootProps) {
  const [darkMode, setDarkMode] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem('darkMode');
      if (stored !== null) {
        return JSON.parse(stored) as boolean;
      }
    } catch {
      // localStorage may be unavailable (private browsing, disabled storage);
      // fall through to the OS preference default below.
    }
    return window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? true;
  });

  // Sync theme mode to document for CSS styling
  useEffect(() => {
    document.documentElement.classList.toggle('dark', darkMode);
  }, [darkMode]);

  const toggleDarkMode = () => {
    setDarkMode((prev: boolean) => {
      const next = !prev;
      try {
        localStorage.setItem('darkMode', JSON.stringify(next));
      } catch {
        // Ignore write failures (private browsing, disabled storage); the
        // toggle still applies for the current session via React state.
      }
      return next;
    });
  };

  return (
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <ClerkProvider
          publishableKey={publishableKey}
          signInUrl="/sign-in"
          afterSignOutUrl="/sign-in"
        >
          <ErrorBoundary>
            <RoutedApp darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
          </ErrorBoundary>
          <Toaster darkMode={darkMode} />
        </ClerkProvider>
      </QueryClientProvider>
    </StrictMode>
  );
}

const root = createRoot(document.getElementById('root')!);
root.render(<div />);

resolveConfig({
  overrides: {
    apiUrl: import.meta.env.VITE_API_BASE_URL || undefined,
    externalApiUrl: import.meta.env.VITE_API_EXTERNAL_BASE_URL || undefined,
    wsUrl: import.meta.env.VITE_WS_URL || undefined,
    clerkPublishableKey: import.meta.env.VITE_CLERK_PUBLISHABLE_KEY || undefined,
    telegramBotName: import.meta.env.VITE_TELEGRAM_BOT_NAME || undefined,
    waitlistUrl: import.meta.env.VITE_WAITLIST_URL || undefined,
  },
})
  .then((config) => {
    setConfig(config);
    initSentry();
    root.render(<Root publishableKey={config.auth.publishableKey} />);
  })
  .catch((err) => {
    root.render(
      <div style={{ padding: '2rem' }}>
        <h2>Configuration Error</h2>
        <p>{err instanceof Error ? err.message : 'Failed to load app configuration.'}</p>
      </div>
    );
  });
